import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import stateAnalyticApp, {
  getDefaultAnalyticsFromDate,
  getDefaultAnalyticsToDate,
} from "../../../state/stateAnalyticApp";
import stateAnalyticCompany from "../../../state/stateAnalyticCompany";
import AnalyticChartData from "../../../api/AnalyticChartData";
import AnalyticIncidentData from "../../../api/AnalyticIncidentData";
import { NoRightsMessage } from "../../UI/NoRightsMessage";
import { AnalyticsFilterBar } from "./AnalyticsFilterBar";
import { GraphConteiner } from "./graphs/GraphConteiner";
import { IncidentCompany } from "./graphs/incidents/IncidentCompany";
import { CardIncident } from "./graphs/incidents/CardIncident";
import { AnalyticsChartSkeleton } from "./skeletons/AnalyticsChartSkeleton";
import { AnalyticsIncidentSkeleton } from "./skeletons/AnalyticsIncidentSkeleton";

const initDateRange = () => {
  if (stateAnalyticApp.from_date && stateAnalyticApp.to_date) return;
  stateAnalyticApp.setParameter("from_date", getDefaultAnalyticsFromDate());
  stateAnalyticApp.setParameter("to_date", getDefaultAnalyticsToDate());
};

const resetAnalyticsState = () => {
  stateAnalyticApp.setParameter("noRight", false);
  stateAnalyticApp.setParameter("loaded_printed_dates", false);
  stateAnalyticApp.setParameter("loaded_printed_count", false);
  stateAnalyticApp.setParameter("loaded_verify_dates", false);
  stateAnalyticApp.setParameter("loaded_verify_count", false);
  stateAnalyticApp.resetChartPhases();
  AnalyticChartData.resetDataSet();
  AnalyticIncidentData.resetIncidents();
};

export const CompanyAnalyticsPanel = observer(({ page, companyId }) => {
  const refApp = useRef(null);
  const abortRef = useRef(null);
  const [panelLoading, setPanelLoading] = React.useState(true);

  useEffect(() => {
    const onResize = () => {
      stateAnalyticApp.handlerResize(window.innerWidth);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!companyId) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    stateAnalyticCompany.setParameter("id_company", companyId);
    resetAnalyticsState();
    initDateRange();
    setPanelLoading(true);

    const run = async () => {
      if (page === "analitic") {
        await AnalyticChartData.getLabelData();
      } else {
        await AnalyticIncidentData.fetchList(ac.signal);
      }
      if (!ac.signal.aborted) {
        setPanelLoading(false);
      }
    };
    run();

    return () => {
      ac.abort();
      AnalyticChartData.abortLabelDataFetch();
    };
  }, [companyId, page]);

  if (stateAnalyticApp.noRight) {
    return (
      <div className="company-card-analytics">
        <NoRightsMessage variant="panel">Недостаточно прав!</NoRightsMessage>
      </div>
    );
  }

  const chartsBusy =
    page === "analitic" &&
    panelLoading &&
    !(
      stateAnalyticApp.loaded_printed_dates &&
      stateAnalyticApp.loaded_printed_count &&
      stateAnalyticApp.loaded_verify_dates &&
      stateAnalyticApp.loaded_verify_count
    );

  const incidentsBusy =
    page === "incident" &&
    panelLoading &&
    !stateAnalyticApp.isLoading &&
    AnalyticIncidentData.incidents === null;

  return (
    <div ref={refApp} className="company-card-analytics" aria-busy={panelLoading}>
      <AnalyticsFilterBar
        page={page}
        onAfterFetch={() => setPanelLoading(false)}
      />
      {page === "analitic" ? (
        chartsBusy ? (
          <AnalyticsChartSkeleton />
        ) : (
          <GraphConteiner refApp={refApp} />
        )
      ) : null}
      {page === "incident" ? (
        <>
          {incidentsBusy ? (
            <AnalyticsIncidentSkeleton />
          ) : !AnalyticIncidentData.incidentCard.visible ? (
            <IncidentCompany />
          ) : (
            <CardIncident />
          )}
        </>
      ) : null}
    </div>
  );
});
